import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import {
  IonAccordion,
  IonAccordionGroup,
  IonIcon,
  IonItem,
  IonLabel,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { cloudDoneOutline } from 'ionicons/icons';

import { ITEM_TYPE_LABEL, ItemType } from '../../models/item-type';
import { SyncService } from '../../sync/sync.service';
import { SyncMessage } from '../../../../../common/enums/sync-message.enum';

/** Plural heading per kind — the rows are counts, not single items. */
const KIND_PLURAL: Record<ItemType, string> = {
  [ItemType.Activity]: 'Activities',
  [ItemType.Group]: 'Groups',
  [ItemType.Sequence]: 'Sequences',
  [ItemType.Program]: 'Programs',
};

const KIND_ORDER: ItemType[] = [
  ItemType.Activity,
  ItemType.Group,
  ItemType.Sequence,
  ItemType.Program,
];

/** One row: how many items of this kind are in the cloud. */
interface BackedUpRow {
  kind: ItemType;
  count: number;
}

/**
 * Backup summary: how much of the library is actually in the cloud, by kind.
 *
 * A projection of `SyncService.cloudItems` — the very list rendered below it —
 * rather than a second, device-side calculation. That is deliberate: the two
 * previously answered "what would the next backup send" and "what is stored",
 * which are different questions with different answers, and putting them one
 * above the other made a correct screen read as a broken one.
 *
 * Counts only, no byte totals. The Cloud size card above already carries the
 * authoritative figure, and the per-item sizes below cannot be summed into a
 * second one — media shared between items would be counted twice by `totalBytes`
 * and by nobody at all by `exclusiveBytes`.
 */
@Component({
  selector: 'app-backup-summary-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonAccordion, IonAccordionGroup, IonIcon, IonItem, IonLabel],
  template: `
    <div class="section-title"><h2>{{ Title }}</h2></div>

    @if (rows(); as list) {
      @if (list.length === 0) {
        <div class="stats-card surface" data-testid="backup-summary">
          <div class="stats-row"><span class="stats-label">{{ Empty }}</span></div>
        </div>
      } @else {
        <!-- Same accordion shape as Settings → Local data: both answer "what is
             stored and how much of it", and a user who has opened one already
             knows how to read the other. -->
        <ion-accordion-group class="settings-accordion" data-testid="backup-summary">
          <ion-accordion value="backed-up">
            <ion-item slot="header" lines="none">
              <ion-icon name="cloud-done-outline" slot="start" aria-hidden="true"></ion-icon>
              <ion-label>
                <div class="item-title">{{ Heading }}</div>
                <p class="header-value">{{ totalLabel() }}</p>
              </ion-label>
            </ion-item>
            <div slot="content" class="stats-card">
              @for (row of list; track row.kind) {
                <div class="stats-row">
                  <span class="stats-label">{{ plural(row.kind) }}</span>
                  <span class="stats-value">{{ row.count }}</span>
                </div>
              }
            </div>
          </ion-accordion>
        </ion-accordion-group>
      }
    } @else {
      <div class="stats-card surface" data-testid="backup-summary">
        <div class="stats-row"><span class="stats-label">Loading…</span></div>
      </div>
    }
  `,
  styles: [`
    .section-title h2 {
      font-size: 14px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--app-text-muted, rgba(0, 0, 0, 0.55));
      margin: 18px 0 8px;
    }

    /* Mirrors .settings-accordion in settings.page.scss — page styles are
       page-scoped, so the shape is repeated rather than reachable from here. */
    .settings-accordion { margin: 0; }
    .settings-accordion ion-accordion {
      margin: 0;
      border-radius: 14px;
      overflow: hidden;
      border: 1px solid var(--app-card-border, var(--app-border));
      background: var(--ion-card-background, var(--app-surface));
      box-shadow: var(--app-card-shadow, none);
    }
    .settings-accordion ion-item[slot="header"] {
      --background: transparent;
      --padding-start: 16px;
      --inner-padding-end: 12px;
      --min-height: 54px;
    }
    .settings-accordion ion-item[slot="header"] ion-icon[slot="start"] {
      color: var(--ion-color-success);
    }
    .item-title { font-weight: 700; letter-spacing: -0.02em; }
    /* The figure people came for, readable without opening the row. */
    .header-value {
      margin: 2px 0 0;
      font-size: 0.85rem;
      color: var(--ion-color-medium);
    }

    .stats-card { margin: 0; padding: 0 16px 6px; }
    .stats-row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 0;
    }
    .stats-row + .stats-row { border-top: 1px solid var(--app-card-border, var(--app-border)); }
    .stats-label { color: var(--ion-color-medium); }
    .stats-value { text-align: end; font-weight: 700; }

    /* The empty and loading states keep the plain card — an accordion with one
       line inside is a control that does nothing. */
    .surface.stats-card { padding: 6px 16px; border-radius: 14px; }
  `],
})
export class BackupSummaryCardComponent {
  private readonly sync = inject(SyncService);

  protected readonly Title = SyncMessage.SummaryTitle;
  protected readonly Heading = SyncMessage.SummaryHeading;
  protected readonly Empty = SyncMessage.SummaryEmpty;

  /** Per-kind counts, empty kinds omitted. Null while the cloud list has never
   *  loaded — an empty list would otherwise read as "nothing backed up" to
   *  someone who is simply offline. */
  protected readonly rows = computed<BackedUpRow[] | null>(() => {
    const items = this.sync.cloudItems();
    if (!items) return null;
    return KIND_ORDER
      .map((kind) => ({ kind, count: items.filter((i) => i.kind === kind).length }))
      .filter((row) => row.count > 0);
  });

  protected readonly totalLabel = computed(() => {
    const total = this.sync.cloudItems()?.length ?? 0;
    return `${total} ${total === 1 ? 'item' : 'items'}`;
  });

  constructor() {
    addIcons({ cloudDoneOutline });
  }

  protected plural(kind: ItemType): string {
    return KIND_PLURAL[kind] ?? ITEM_TYPE_LABEL[kind];
  }
}
